import type { DropDownOptionBase } from "../components/staff/DropDownMultiple";
import type {
  IAccountingFees,
  IAccountingFeesDetails,
} from "../interfaces/models/IAccountingFees";
import { billingPeriodRangeLabel } from "./billingYear";
import {
  DEFAULT_SPECIAL_FUND_NAME,
  DEFAULT_SPECIAL_FUND_PERCENTAGE,
  resolveSpecialFundFromDoc,
  upsertSpecialFundDetail,
} from "./accountingSpecialFund";
import {
  resolveAnnualMeetingRegFeeFormValue,
  upsertAnnualMeetingRegFeeDetail,
} from "./accountingAnnualMeetingFee";

type ProfessionalsRange = NonNullable<
  IAccountingFeesDetails["numberOfProfessionals"]
>;

type InvestmentBankingRange = NonNullable<
  IAccountingFeesDetails["investmentBanking"]
>;

type DetailType = IAccountingFeesDetails["type"];

const LEGACY_MEMBERSHIP_TYPE = "membershipLegalAndAccounting";

type MembershipLoadType = "membershipLegal" | "membershipAccounting";

export type TariffRangeRow = {
  range: string;
  rangeKey: string;
  storedFee: number;
  currentFee: number;
  previousBase: number | null;
};

export type ManageFiscalYearFormState = {
  initiationIncrease: string;
  legalIncrease: string;
  accountingIncrease: string;
  investmentIncrease: string;
  initiationRows: TariffRangeRow[];
  legalRows: TariffRangeRow[];
  accountingRows: TariffRangeRow[];
  investmentRows: TariffRangeRow[];
  investmentShowRangeColumn: boolean;
  annualMeetingFee: string;
  specialFundName: string;
  specialFundPercentage: string;
  syncAccountingWithLegal: boolean;
  prevYearInitiationRef: string;
  prevYearLegalRef: string;
  prevYearAccountingRef: string;
  prevYearInvestmentRef: string;
};

export type AccountingFeesYearIndex = Map<
  number,
  { normal?: IAccountingFees; annualMeeting?: IAccountingFees }
>;

const PROFESSIONALS_RANGE_ORDER: ProfessionalsRange[] = [
  "1-10",
  "11-20",
  "21-40",
  "41-60",
  "61-80",
  "81-100",
  "101-150",
  "151-200",
  "201-300",
  "301-400",
  "401-500",
  "501-600",
  "601-700",
  "701-800",
  "801-900",
  "901-1000",
  "1000+",
];

const INVESTMENT_BANKING_RANGE_ORDER: InvestmentBankingRange[] = [
  "OneFee",
  "<=1mill",
  "1mill-4mill",
  "4mill-7mill",
  "7mill-10mill",
  "10mill-13mill",
  "13mill+",
];

const INVESTMENT_BANKING_RANGE_LABELS: Record<InvestmentBankingRange, string> =
  {
    OneFee: "One Fee",
    "<=1mill": "Less than $1 million",
    "1mill-4mill": "$1 million to $4 million",
    "4mill-7mill": "$4 million to $7 million",
    "7mill-10mill": "$7 million to $10 million",
    "10mill-13mill": "$10 million to $13 million",
    "13mill+": "More than $13 million",
  };

const EMPTY_FORM_STATE: ManageFiscalYearFormState = {
  initiationIncrease: "",
  legalIncrease: "",
  accountingIncrease: "",
  investmentIncrease: "",
  initiationRows: [],
  legalRows: [],
  accountingRows: [],
  investmentRows: [],
  investmentShowRangeColumn: false,
  annualMeetingFee: "",
  specialFundName: DEFAULT_SPECIAL_FUND_NAME,
  specialFundPercentage: String(DEFAULT_SPECIAL_FUND_PERCENTAGE),
  syncAccountingWithLegal: true,
  prevYearInitiationRef: "",
  prevYearLegalRef: "",
  prevYearAccountingRef: "",
  prevYearInvestmentRef: "",
};

export function formatProfessionalsRangeLabel(range: string): string {
  return `${range} Professionals`;
}

export function parseIncreasePercent(value: string): number {
  const parsed = parseFloat(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Round fee up to the next $10 when the amount is not already a multiple of 10. */
export function roundCalculatedFeeToTens(amount: number): number {
  if (!Number.isFinite(amount)) {
    return 0;
  }
  const rounded = Math.round(amount);
  if (rounded % 10 === 0) {
    return rounded;
  }
  return Math.ceil(rounded / 10) * 10;
}

export function calculateFeeFromIncrease(
  base: number | null,
  storedFee: number,
  increasePercent: number,
): number {
  const feeBase = base ?? storedFee;
  if (!Number.isFinite(feeBase)) {
    return 0;
  }
  const calculated = feeBase + feeBase * (increasePercent / 100);
  return roundCalculatedFeeToTens(calculated);
}

export function applyIncreaseToRows(
  rows: TariffRangeRow[],
  increaseValue: string,
): TariffRangeRow[] {
  const increasePercent = parseIncreasePercent(increaseValue);
  return rows.map((row) => ({
    ...row,
    currentFee: calculateFeeFromIncrease(
      row.previousBase,
      row.storedFee,
      increasePercent,
    ),
  }));
}

export type SaveFiscalYearFeesPayload = {
  normal?: IAccountingFees;
  createNormal?: IAccountingFees;
};

function formatIncreaseValue(value: number | undefined | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "";
  }
  return String(value);
}

function prevYearRefLabel(
  year: number,
  increasePercentage: number | null | undefined,
): string {
  const prevPeriodLabel = billingPeriodRangeLabel(year - 2);
  if (increasePercentage === null || increasePercentage === undefined) {
    return `Prev. year ref. (${prevPeriodLabel}) = —`;
  }
  return `Prev. year ref. (${prevPeriodLabel}) = ${increasePercentage}%`;
}

function firstIncreaseForType(
  doc: IAccountingFees | undefined,
  detailType: DetailType | MembershipLoadType,
): number | null {
  const detail = doc?.details?.find((d) => d.type === detailType);
  return detail?.increasePercentage ?? null;
}

function firstIncreaseForMembershipType(
  doc: IAccountingFees | undefined,
  detailType: MembershipLoadType,
): number | null {
  return (
    firstIncreaseForType(doc, detailType) ??
    doc?.details?.find((d) => String(d.type) === LEGACY_MEMBERSHIP_TYPE)
      ?.increasePercentage ??
    null
  );
}

function findFeeByProfessionalsRange(
  doc: IAccountingFees | undefined,
  detailType: DetailType | MembershipLoadType | typeof LEGACY_MEMBERSHIP_TYPE,
  range: ProfessionalsRange,
): number | null {
  const detail = doc?.details?.find(
    (d) => String(d.type) === detailType && d.numberOfProfessionals === range,
  );
  return detail?.fee ?? null;
}

function findMembershipFeeByProfessionalsRange(
  doc: IAccountingFees | undefined,
  detailType: MembershipLoadType,
  range: ProfessionalsRange,
): number | null {
  return (
    findFeeByProfessionalsRange(doc, detailType, range) ??
    findFeeByProfessionalsRange(doc, LEGACY_MEMBERSHIP_TYPE, range)
  );
}

function findFeeByInvestmentRange(
  doc: IAccountingFees | undefined,
  range: InvestmentBankingRange,
): number | null {
  const detail = doc?.details?.find(
    (d) =>
      d.type === "membershipInvestmentBanking" &&
      d.investmentBanking === range,
  );
  return detail?.fee ?? null;
}

function buildProfessionalsRowsFromSourceDoc(
  sourceDoc: IAccountingFees,
  detailType: DetailType | MembershipLoadType,
): TariffRangeRow[] {
  const rangesInDoc = new Set<ProfessionalsRange>();

  for (const detail of sourceDoc.details) {
    if (!detail.numberOfProfessionals) {
      continue;
    }
    if (detail.type === detailType) {
      rangesInDoc.add(detail.numberOfProfessionals);
      continue;
    }
    if (
      (detailType === "membershipLegal" ||
        detailType === "membershipAccounting") &&
      String(detail.type) === LEGACY_MEMBERSHIP_TYPE
    ) {
      rangesInDoc.add(detail.numberOfProfessionals);
    }
  }

  return PROFESSIONALS_RANGE_ORDER.filter((range) => rangesInDoc.has(range)).map(
    (range) => {
      const previousBase =
        (detailType === "membershipLegal" ||
        detailType === "membershipAccounting"
          ? findMembershipFeeByProfessionalsRange(sourceDoc, detailType, range)
          : findFeeByProfessionalsRange(sourceDoc, detailType, range)) ?? 0;
      return {
        range: formatProfessionalsRangeLabel(range),
        rangeKey: range,
        storedFee: previousBase,
        currentFee: previousBase,
        previousBase,
      };
    },
  );
}

function buildInvestmentRowsFromSourceDoc(sourceDoc: IAccountingFees): {
  rows: TariffRangeRow[];
  showRangeColumn: boolean;
} {
  const rangesInDoc = new Set(
    sourceDoc.details
      .filter(
        (d) => d.type === "membershipInvestmentBanking" && d.investmentBanking,
      )
      .map((d) => d.investmentBanking as InvestmentBankingRange),
  );

  const orderedRanges = INVESTMENT_BANKING_RANGE_ORDER.filter((range) =>
    rangesInDoc.has(range),
  );

  const showRangeColumn = !(
    orderedRanges.length === 1 && orderedRanges[0] === "OneFee"
  );

  const rows = orderedRanges.map((range) => {
    const previousBase = findFeeByInvestmentRange(sourceDoc, range) ?? 0;
    return {
      range: INVESTMENT_BANKING_RANGE_LABELS[range],
      rangeKey: range,
      storedFee: previousBase,
      currentFee: previousBase,
      previousBase,
    };
  });

  return { rows, showRangeColumn };
}

export function resolveNextFiscalYear(
  index: AccountingFeesYearIndex,
  _draftYears: number[],
): number | null {
  const dbYears = [...index.entries()]
    .filter(([, entry]) => entry.normal)
    .map(([year]) => year);
  if (dbYears.length === 0) {
    return null;
  }
  return Math.max(...dbYears) + 1;
}

export function appendDraftFiscalYearOption(
  options: DropDownOptionBase[],
  year: number,
): DropDownOptionBase[] {
  if (options.some((option) => Number(option.id) === year)) {
    return options;
  }
  const draftOption: DropDownOptionBase = {
    id: String(year),
    name: `${billingPeriodRangeLabel(year - 1)} (New)`,
  };
  return [draftOption, ...options].sort(
    (a, b) => Number(b.id) - Number(a.id),
  );
}

export function isDraftFiscalYear(
  year: number,
  index: AccountingFeesYearIndex,
  draftYears: number[],
): boolean {
  return draftYears.includes(year) && !index.get(year)?.normal;
}

export function mapNewFiscalYearFormState(
  year: number,
  index: AccountingFeesYearIndex,
): ManageFiscalYearFormState | null {
  const sourceDoc = index.get(year - 1)?.normal;
  if (!sourceDoc?.details?.length) {
    return null;
  }

  const prevDoc = index.get(year - 2)?.normal;
  const { rows: investmentRows, showRangeColumn: investmentShowRangeColumn } =
    buildInvestmentRowsFromSourceDoc(sourceDoc);

  return {
    initiationIncrease: "0",
    legalIncrease: "0",
    accountingIncrease: "0",
    investmentIncrease: "0",
    initiationRows: buildProfessionalsRowsFromSourceDoc(
      sourceDoc,
      "initiationLegalAndAccounting",
    ),
    legalRows: buildProfessionalsRowsFromSourceDoc(
      sourceDoc,
      "membershipLegal",
    ),
    accountingRows: buildProfessionalsRowsFromSourceDoc(
      sourceDoc,
      "membershipAccounting",
    ),
    investmentRows,
    investmentShowRangeColumn,
    annualMeetingFee: resolveAnnualMeetingFee(year - 1, index),
    ...(() => {
      const fund = resolveSpecialFundFromDoc(sourceDoc);
      return {
        specialFundName: fund.specialFundName,
        specialFundPercentage: String(fund.specialFundPercentage),
      };
    })(),
    syncAccountingWithLegal: true,
    prevYearInitiationRef: prevYearRefLabel(
      year,
      firstIncreaseForType(sourceDoc, "initiationLegalAndAccounting"),
    ),
    prevYearLegalRef: prevYearRefLabel(
      year,
      firstIncreaseForMembershipType(sourceDoc, "membershipLegal"),
    ),
    prevYearAccountingRef: prevYearRefLabel(
      year,
      firstIncreaseForMembershipType(sourceDoc, "membershipAccounting"),
    ),
    prevYearInvestmentRef: prevYearRefLabel(
      year,
      firstIncreaseForType(sourceDoc, "membershipInvestmentBanking"),
    ),
  };
}

function buildProfessionalsRows(
  year: number,
  index: AccountingFeesYearIndex,
  detailType: DetailType | MembershipLoadType,
): TariffRangeRow[] {
  const currentDoc = index.get(year)?.normal;
  const prevDoc = index.get(year - 1)?.normal;
  if (!currentDoc?.details?.length) {
    return [];
  }

  const rangesInDoc = new Set<ProfessionalsRange>();
  for (const detail of currentDoc.details) {
    if (!detail.numberOfProfessionals) {
      continue;
    }
    if (detail.type === detailType) {
      rangesInDoc.add(detail.numberOfProfessionals);
      continue;
    }
    if (
      (detailType === "membershipLegal" ||
        detailType === "membershipAccounting") &&
      String(detail.type) === LEGACY_MEMBERSHIP_TYPE
    ) {
      rangesInDoc.add(detail.numberOfProfessionals);
    }
  }

  return PROFESSIONALS_RANGE_ORDER.filter((range) => rangesInDoc.has(range)).map(
    (range) => {
      const currentFee =
        (detailType === "membershipLegal" ||
        detailType === "membershipAccounting"
          ? findMembershipFeeByProfessionalsRange(currentDoc, detailType, range)
          : findFeeByProfessionalsRange(currentDoc, detailType, range)) ?? 0;
      const previousBase = prevDoc
        ? detailType === "membershipLegal" ||
          detailType === "membershipAccounting"
          ? findMembershipFeeByProfessionalsRange(prevDoc, detailType, range)
          : findFeeByProfessionalsRange(prevDoc, detailType, range)
        : null;
      return {
        range: formatProfessionalsRangeLabel(range),
        rangeKey: range,
        storedFee: currentFee,
        currentFee,
        previousBase,
      };
    },
  );
}

function buildInvestmentRows(
  year: number,
  index: AccountingFeesYearIndex,
): { rows: TariffRangeRow[]; showRangeColumn: boolean } {
  const currentDoc = index.get(year)?.normal;
  const prevDoc = index.get(year - 1)?.normal;
  if (!currentDoc?.details?.length) {
    return { rows: [], showRangeColumn: false };
  }

  const rangesInDoc = new Set(
    currentDoc.details
      .filter(
        (d) => d.type === "membershipInvestmentBanking" && d.investmentBanking,
      )
      .map((d) => d.investmentBanking as InvestmentBankingRange),
  );

  const orderedRanges = INVESTMENT_BANKING_RANGE_ORDER.filter((range) =>
    rangesInDoc.has(range),
  );

  const showRangeColumn = !(
    orderedRanges.length === 1 && orderedRanges[0] === "OneFee"
  );

  const rows = orderedRanges.map((range) => {
    const currentFee = findFeeByInvestmentRange(currentDoc, range) ?? 0;
    const previousBase = prevDoc
      ? findFeeByInvestmentRange(prevDoc, range)
      : null;
    return {
      range: INVESTMENT_BANKING_RANGE_LABELS[range],
      rangeKey: range,
      storedFee: currentFee,
      currentFee,
      previousBase,
    };
  });

  return { rows, showRangeColumn };
}

function resolveAnnualMeetingFee(
  year: number,
  index: AccountingFeesYearIndex,
): string {
  const entry = index.get(year);
  return resolveAnnualMeetingRegFeeFormValue(
    entry?.normal,
    entry?.annualMeeting,
  );
}

function finalizeNormalDocumentDetails(
  details: IAccountingFeesDetails[],
  formState: ManageFiscalYearFormState,
  options: {
    syncAccountingWithLegal: boolean;
    initiationRows: TariffRangeRow[];
    legalRows: TariffRangeRow[];
    accountingRows: TariffRangeRow[];
    investmentRows: TariffRangeRow[];
  },
): IAccountingFeesDetails[] {
  return upsertAnnualMeetingRegFeeDetail(
    upsertSpecialFundDetail(
      applyCalculatedFeesToDetails(
        normalizeMembershipDetails(details),
        formState,
        options,
      ),
      formState.specialFundName,
      parseFloat(formState.specialFundPercentage) ||
        DEFAULT_SPECIAL_FUND_PERCENTAGE,
    ),
    formState.annualMeetingFee,
  );
}

export function indexAccountingFeesByYear(
  docs: IAccountingFees[],
): AccountingFeesYearIndex {
  const index: AccountingFeesYearIndex = new Map();

  for (const doc of docs) {
    const year = doc.year;
    if (!year) {
      continue;
    }

    const entry = index.get(year) ?? {};
    if (doc.type === "annualMeetingPrepaid") {
      entry.annualMeeting = doc;
    } else if ((doc.type ?? "normal") === "normal") {
      entry.normal = doc;
    }
    index.set(year, entry);
  }

  return index;
}

export function buildFiscalYearOptions(
  docs: IAccountingFees[],
  currentFiscalYear: number,
): DropDownOptionBase[] {
  const years = docs
    .filter((doc) => (doc.type ?? "normal") === "normal" && doc.year)
    .map((doc) => doc.year)
    .sort((a, b) => b - a);

  const uniqueYears = [...new Set(years)];

  return uniqueYears.map((year) => ({
    id: String(year),
    name:
      year === currentFiscalYear
        ? `${billingPeriodRangeLabel(year - 1)} (Current)`
        : billingPeriodRangeLabel(year - 1),
  }));
}

export function resolveDefaultFiscalYear(
  options: DropDownOptionBase[],
  currentFiscalYear: number,
): number | null {
  if (options.length === 0) {
    return null;
  }
  const current = options.find((o) => Number(o.id) === currentFiscalYear);
  if (current) {
    return currentFiscalYear;
  }
  return Number(options[0].id);
}

export function mapFeesToFormState(
  year: number,
  index: AccountingFeesYearIndex,
): ManageFiscalYearFormState {
  const currentDoc = index.get(year)?.normal;
  const prevDoc = index.get(year - 1)?.normal;

  if (!currentDoc) {
    return { ...EMPTY_FORM_STATE };
  }

  const initiationIncrease = firstIncreaseForType(
    currentDoc,
    "initiationLegalAndAccounting",
  );
  const legalIncrease = firstIncreaseForMembershipType(
    currentDoc,
    "membershipLegal",
  );
  const accountingIncrease = firstIncreaseForMembershipType(
    currentDoc,
    "membershipAccounting",
  );
  const investmentIncrease = firstIncreaseForType(
    currentDoc,
    "membershipInvestmentBanking",
  );

  const { rows: investmentRows, showRangeColumn: investmentShowRangeColumn } =
    buildInvestmentRows(year, index);

  return {
    initiationIncrease: formatIncreaseValue(initiationIncrease),
    legalIncrease: formatIncreaseValue(legalIncrease),
    accountingIncrease: formatIncreaseValue(accountingIncrease),
    investmentIncrease: formatIncreaseValue(investmentIncrease),
    initiationRows: buildProfessionalsRows(
      year,
      index,
      "initiationLegalAndAccounting",
    ),
    legalRows: buildProfessionalsRows(
      year,
      index,
      "membershipLegal",
    ),
    accountingRows: buildProfessionalsRows(
      year,
      index,
      "membershipAccounting",
    ),
    investmentRows,
    investmentShowRangeColumn,
    annualMeetingFee: resolveAnnualMeetingFee(year, index),
    ...(() => {
      const fund = resolveSpecialFundFromDoc(currentDoc);
      return {
        specialFundName: fund.specialFundName,
        specialFundPercentage: String(fund.specialFundPercentage),
      };
    })(),
    syncAccountingWithLegal: true,
    prevYearInitiationRef: prevYearRefLabel(
      year,
      firstIncreaseForType(prevDoc, "initiationLegalAndAccounting"),
    ),
    prevYearLegalRef: prevYearRefLabel(
      year,
      firstIncreaseForMembershipType(prevDoc, "membershipLegal"),
    ),
    prevYearAccountingRef: prevYearRefLabel(
      year,
      firstIncreaseForMembershipType(prevDoc, "membershipAccounting"),
    ),
    prevYearInvestmentRef: prevYearRefLabel(
      year,
      firstIncreaseForType(prevDoc, "membershipInvestmentBanking"),
    ),
  };
}

function feeByRangeKey(
  rows: TariffRangeRow[],
  rangeKey: string,
): number | undefined {
  return rows.find((row) => row.rangeKey === rangeKey)?.currentFee;
}

function normalizeMembershipDetails(
  details: IAccountingFeesDetails[],
): IAccountingFeesDetails[] {
  const normalized: IAccountingFeesDetails[] = [];
  for (const detail of details) {
    if (String(detail.type) === LEGACY_MEMBERSHIP_TYPE) {
      normalized.push({ ...detail, type: "membershipLegal" });
      normalized.push({ ...detail, type: "membershipAccounting" });
      continue;
    }
    normalized.push(detail);
  }
  return normalized;
}

function applyCalculatedFeesToDetails(
  details: IAccountingFeesDetails[],
  formState: ManageFiscalYearFormState,
  options: {
    syncAccountingWithLegal: boolean;
    initiationRows: TariffRangeRow[];
    legalRows: TariffRangeRow[];
    accountingRows: TariffRangeRow[];
    investmentRows: TariffRangeRow[];
  },
): IAccountingFeesDetails[] {
  const initiationIncrease = parseIncreasePercent(formState.initiationIncrease);
  const legalIncrease = parseIncreasePercent(formState.legalIncrease);
  const accountingIncrease = parseIncreasePercent(formState.accountingIncrease);
  const investmentIncrease = parseIncreasePercent(formState.investmentIncrease);
  const membershipIncrease = options.syncAccountingWithLegal
    ? legalIncrease
    : accountingIncrease;

  return details.map((detail) => {
    if (detail.type === "specialFund" || detail.type === "annualMeetingPrepaid") {
      return detail;
    }

    if (
      detail.type === "initiationLegalAndAccounting" &&
      detail.numberOfProfessionals
    ) {
      const fee = feeByRangeKey(
        options.initiationRows,
        detail.numberOfProfessionals,
      );
      if (fee !== undefined) {
        return {
          ...detail,
          fee,
          increasePercentage: initiationIncrease,
        };
      }
    }

    if (detail.type === "membershipLegal" && detail.numberOfProfessionals) {
      const fee = feeByRangeKey(options.legalRows, detail.numberOfProfessionals);
      if (fee !== undefined) {
        return {
          ...detail,
          fee,
          increasePercentage: legalIncrease,
        };
      }
    }

    if (
      detail.type === "membershipAccounting" &&
      detail.numberOfProfessionals
    ) {
      const fee = options.syncAccountingWithLegal
        ? feeByRangeKey(options.legalRows, detail.numberOfProfessionals)
        : feeByRangeKey(
            options.accountingRows,
            detail.numberOfProfessionals,
          );
      if (fee !== undefined) {
        return {
          ...detail,
          fee,
          increasePercentage: membershipIncrease,
        };
      }
    }

    if (
      detail.type === "membershipInvestmentBanking" &&
      detail.investmentBanking
    ) {
      const fee = feeByRangeKey(
        options.investmentRows,
        detail.investmentBanking,
      );
      if (fee !== undefined) {
        return {
          ...detail,
          fee,
          increasePercentage: investmentIncrease,
        };
      }
    }

    return detail;
  });
}

export function buildCreateFiscalYearFeesPayload(
  year: number,
  index: AccountingFeesYearIndex,
  formState: ManageFiscalYearFormState,
  options: {
    syncAccountingWithLegal: boolean;
    initiationRows: TariffRangeRow[];
    legalRows: TariffRangeRow[];
    accountingRows: TariffRangeRow[];
    investmentRows: TariffRangeRow[];
  },
): SaveFiscalYearFeesPayload | null {
  const sourceDoc = index.get(year - 1)?.normal;
  if (!sourceDoc?.details?.length) {
    return null;
  }

  return {
    createNormal: {
      year,
      type: "normal",
      details: finalizeNormalDocumentDetails(
        sourceDoc.details,
        formState,
        options,
      ),
      createdDate: Math.floor(Date.now() / 1000),
    },
  };
}

export function buildSaveFiscalYearFeesPayload(
  year: number,
  index: AccountingFeesYearIndex,
  formState: ManageFiscalYearFormState,
  options: {
    syncAccountingWithLegal: boolean;
    initiationRows: TariffRangeRow[];
    legalRows: TariffRangeRow[];
    accountingRows: TariffRangeRow[];
    investmentRows: TariffRangeRow[];
  },
): SaveFiscalYearFeesPayload | null {
  const normalDoc = index.get(year)?.normal;
  if (!normalDoc?.id) {
    return null;
  }

  return {
    normal: {
      ...normalDoc,
      details: finalizeNormalDocumentDetails(
        normalDoc.details,
        formState,
        options,
      ),
    },
  };
}
